import { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch } from "react-redux";
import { getBenefitList } from "../api/plan";
import { generateTokenByApiKey } from "../api/login";

const useBenefits = (tenantId) => {
  const dispatch = useDispatch();
  const [benefits, setBenefits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const requestId = useRef(0);

  const getAccessToken = useCallback(
    (onToken, onFail) => {
      let keys;
      try {
        keys = JSON.parse(process.env.REACT_APP_API_KEYS || "[]");
      } catch {
        keys = [];
      }
      const key = keys.find((f) => f.tenantId === tenantId);

      if (!key) {
        onFail?.("Missing API key for this tenant.");
        return;
      }

      dispatch(
        generateTokenByApiKey(
          {},
          "zealthix",
          {
            "x-tenant-id": key.tenantId,
            "X-Api-Key": key.key,
            "Content-Type": "application/json",
          },
          (t) => {
            if (!t?.access_token) {
              onFail?.("Could not authenticate.");
              return;
            }
            onToken(t.access_token);
          }
        )
      );
    },
    [dispatch, tenantId]
  );

  const fetchBenefits = useCallback(() => {
    if (!tenantId) {
      setBenefits([]);
      setLoading(false);
      setError("Missing tenantId - cannot load benefits.");
      return;
    }

    const currentRequest = ++requestId.current;
    setLoading(true);
    setError(null);

    getAccessToken(
      (accessToken) => {
        if (currentRequest !== requestId.current) return;

        dispatch(
          getBenefitList({ count: true, size: 10000 }, accessToken, (res) => {
            if (currentRequest !== requestId.current) return;

            const data = Array.isArray(res)
              ? res
              : Array.isArray(res?.data)
              ? res.data
              : [];

            setBenefits(data);
            setLoading(false);
            setError(null);
          })
        );

        setTimeout(() => {
          if (currentRequest === requestId.current) {
            setLoading((prevLoading) => {
              if (prevLoading) {
                setError("Couldn't load benefits right now. Please try again.");
                return false;
              }
              return prevLoading;
            });
          }
        }, 15000);
      },
      (errMsg) => {
        if (currentRequest !== requestId.current) return;
        setBenefits([]);
        setLoading(false);
        setError(errMsg);
      }
    );
  }, [tenantId, dispatch, getAccessToken]);

  useEffect(() => {
    fetchBenefits();
  }, [fetchBenefits]);

  const safeBenefits = Array.isArray(benefits)
    ? benefits.filter((b) => b?.status === "PUBLISHED" && b?.benefitAmount != null)
    : [];

  return { benefits: safeBenefits, loading, error, refetch: fetchBenefits };
};

export default useBenefits;
